import { type MouseEvent, type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectBadge } from '@/features/projects/components/ProjectBadge';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectsQuery } from '@/features/projects/hooks/projects';
import { buildProjectSettingsHref } from '@/features/projects/utils/projectSettingsRoute';
import { s, typographyTokens } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import {
  settingsPageTypographySx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import {
  Button,
  ExpandIcon,
  ListItemText,
  MenuItem,
  MenuList,
  PageShell,
  Popover,
  WorkOutline,
} from '@/ui/primitives';
import type { ProjectDto } from '@flowqa/shared';

type ProjectSettingsProjectPickerProps = {
  selectedOrganisationId: string | null;
};

const projectMenuItemHeight = 42;
const contextButtonHeight = 34;

const projectIconFrameSx = {
  width: 20,
  height: 20,
  borderRadius: s('xs'),
  backgroundColor: 'transparent',
  color: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

export const ProjectSettingsProjectPicker = ({
  selectedOrganisationId,
}: ProjectSettingsProjectPickerProps): ReactElement => {
  const navigate = useNavigate();
  const { setSelectedProjectId } = useProjectFilter();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedPickerProjectId, setSelectedPickerProjectId] = useState<string | null>(null);
  const projectsQuery = useProjectsQuery({
    enabled: selectedOrganisationId !== null,
    organisationId: selectedOrganisationId,
    refetchOnWindowFocus: true,
  });
  const handleOpenProjectSettings = (project: ProjectDto): void => {
    setSelectedProjectId(project.id);
    void navigate(buildProjectSettingsHref(project.id));
  };
  const projects = projectsQuery.data?.items ?? [];
  const hasProjects = projects.length > 0;
  const selectedPickerProject =
    selectedPickerProjectId === null
      ? null
      : (projects.find((project) => project.id === selectedPickerProjectId) ?? null);
  const isLoadingProjects = selectedOrganisationId !== null && projectsQuery.isPending;
  const projectsErrorMessage =
    projectsQuery.error instanceof Error ? projectsQuery.error.message : null;
  const selectedProjectLabel = selectedPickerProject?.name ?? 'Select Project';
  const selectedProjectIcon =
    selectedPickerProject?.badgeEnabled === true ? (
      <ProjectBadge
        chars={selectedPickerProject.badgeChars}
        color={selectedPickerProject.badgeColor}
      />
    ) : (
      <Box component="span" sx={projectIconFrameSx}>
        <WorkOutline sx={{ fontSize: 17, stroke: 'currentColor', strokeWidth: 0.08 }} />
      </Box>
    );
  const closeMenu = (): void => {
    setAnchorEl(null);
  };

  return (
    <PageShell maxWidth="lg" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Stack
        gap="xl"
        sx={[
          settingsPageTypographySx,
          {
            minHeight: { xs: 'calc(100dvh - 220px)', md: 'calc(100dvh - 260px)' },
            justifyContent: 'center',
          },
        ]}
      >
        <Stack gap="md" sx={{ width: '100%', maxWidth: '360px', mx: 'auto' }}>
          <Panel
            title="Project Settings"
            contentGap="lg"
            surfaceVariant="settings"
            sx={settingsPanelSurfaceSx}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                alignItems: 'center',
                gap: s('md'),
                width: '100%',
              }}
            >
              {isLoadingProjects ? (
                <Text role="body" color="textSecondary">
                  Loading projects...
                </Text>
              ) : projectsErrorMessage ? (
                <Text role="body" color="textSecondary">
                  {projectsErrorMessage}
                </Text>
              ) : hasProjects ? (
                <>
                  <Box
                    component="button"
                    type="button"
                    onClick={(event: MouseEvent<HTMLElement>) => {
                      setAnchorEl(event.currentTarget);
                    }}
                    sx={(theme) => ({
                      appearance: 'none',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: theme.app.colors.textMuted,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: s('md'),
                      height: contextButtonHeight,
                      minWidth: 0,
                      width: 'fit-content',
                      maxWidth: '100%',
                      px: s('none'),
                      borderRadius: s('sm'),
                      cursor: 'pointer',
                      font: 'inherit',
                      '&:hover': { backgroundColor: 'transparent' },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.app.state.focus}`,
                        outlineOffset: 2,
                      },
                    })}
                  >
                    <Box
                      component="span"
                      sx={(theme) => ({
                        color: theme.app.colors.textMuted,
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      })}
                    >
                      {selectedProjectIcon}
                    </Box>
                    <Text
                      role="bodyStrong"
                      component="span"
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                        fontSize: 15.5,
                        fontWeight: typographyTokens.label.fontWeight,
                        color: (theme) => theme.app.colors.textMuted,
                      }}
                    >
                      {selectedProjectLabel}
                    </Text>
                    <ExpandIcon
                      sx={{
                        fontSize: 17,
                        flexShrink: 0,
                        color: (theme) => theme.app.colors.textMuted,
                        ml: s('xxs'),
                      }}
                    />
                  </Box>
                  <Button
                    variant="primary"
                    fullWidthOnMobile={false}
                    disabled={selectedPickerProject === null}
                    onClick={() => {
                      if (selectedPickerProject === null) return;
                      handleOpenProjectSettings(selectedPickerProject);
                    }}
                  >
                    Open
                  </Button>
                  <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={closeMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    paperSx={(theme) => ({
                      mt: s('xs'),
                      width: 'max-content',
                      minWidth: 0,
                      border: `1px solid ${theme.app.colors.border}`,
                      borderRadius: '4px !important',
                      backgroundColor: theme.app.colors.surface,
                      backgroundImage: 'none',
                      boxShadow: 'none',
                      overflow: 'hidden',
                    })}
                  >
                    <MenuList
                      dense
                      disablePadding
                      sx={{
                        py: s('none'),
                        '& .MuiMenuItem-root': {
                          minHeight: `${projectMenuItemHeight}px`,
                          px: s('xl'),
                          py: s('sm'),
                        },
                      }}
                    >
                      {projects.map((project) => {
                        const projectBadge = project.badgeEnabled ? (
                          <ProjectBadge chars={project.badgeChars} color={project.badgeColor} />
                        ) : null;

                        return (
                          <MenuItem
                            key={project.id}
                            selected={project.id === selectedPickerProjectId}
                            onClick={() => {
                              setSelectedPickerProjectId(project.id);
                              closeMenu();
                            }}
                          >
                            <ListItemText
                              primary={project.name}
                              sx={{ minWidth: 0, pr: projectBadge === null ? 0 : s('xl') }}
                            />
                            {projectBadge === null ? null : (
                              <Box component="span" sx={{ ml: 'auto', flexShrink: 0 }}>
                                {projectBadge}
                              </Box>
                            )}
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </Popover>
                </>
              ) : (
                <Text role="body" color="textSecondary">
                  Create a project to manage project settings.
                </Text>
              )}
            </Box>
          </Panel>
        </Stack>
      </Stack>
    </PageShell>
  );
};
